import {Component, OnInit, DestroyRef, inject} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BenefitPlanTemplateModel, CoverageLevelModel, MainPageDetailModel} from 'src/app/models/plans.model';
import {MarketingDataRequest} from '../../models/marketingData.request';
import {MarketingDetailsApiResponseInterface} from '../../models/marketing-detail.model';
import {SalesService} from '../../services/sales.service';
import {EventsManager} from '../../events/events.manager';
import {PlanViewTypeEnum} from '../../enums/plan-view-type.enum';

@Component({
  selector: 'app-plan-detail',
  templateUrl: './plan-detail.component.html',
  styleUrls: ['./plan-detail.component.scss'],
  standalone: false
})

export class PlanDetailComponent implements OnInit {
  public eventsManager: EventsManager = inject(EventsManager);
  private _salesService: SalesService = inject(SalesService);
  private _destroyRef: DestroyRef = inject(DestroyRef);
  private _route: ActivatedRoute = inject(ActivatedRoute);

  public readonly PlanViewType: typeof PlanViewTypeEnum = PlanViewTypeEnum;
  private _planInfoDetails: Record<string, string> = {};
  public officeData?: MainPageDetailModel = {} as MainPageDetailModel;
  public details: MarketingDetailsApiResponseInterface | null = null;
  public template?: BenefitPlanTemplateModel = {} as BenefitPlanTemplateModel;
  public coverageLevel?: CoverageLevelModel;

  public ngOnInit(): void {
    window.scrollTo(0, 0);
    this._hydrateFromUrlIfPresent();
    this._planInfoDetails = this.eventsManager.planDetailInfo;
    this.officeData = this.eventsManager.officeInfo;
    this._loadMarketingDetails();
  }

  // PLANS-22888: on refresh EventsManager is a fresh instance ({}), so seed it from the URL when
  // all four params are present. Partial or absent URL falls back to the existing EventsManager path.
  private _hydrateFromUrlIfPresent(): void {
    const queryParams = this._route.snapshot.queryParamMap;
    const practiceId = queryParams.get('practiceId');
    const templateId = queryParams.get('templateId');
    const coverageLevelName = queryParams.get('coverageLevelName');
    const pageName = queryParams.get('pageName');

    if (!practiceId || !templateId || !coverageLevelName || !pageName) {
      return;
    }

    this.eventsManager.setPlanDetails({practiceId, templateId, pageName});

    const office = this.eventsManager.getOfficeInfo();
    const template = office?.benefitPlanTemplates?.find(
      (candidate: BenefitPlanTemplateModel) => candidate.benefitPlanTemplateId === templateId
    );
    const level = template?.coverageLevels?.find(
      (candidate: CoverageLevelModel) => candidate.caption === coverageLevelName
    );
    if (template && level) {
      this.eventsManager.setLevelSelected({...level, planName: template.name});
    }
  }

  public get marketedCoverageLevel(): CoverageLevelModel | undefined {
    const selectedId: string | null | undefined = this.eventsManager.selectedLevel.benefitPlanCoverageLevelId;
    if (!selectedId) {
      return undefined;
    }
    return this.details?.benefitPlanTemplate?.coverageLevels?.find(
      (level: CoverageLevelModel) => level.benefitPlanCoverageLevelId === selectedId
    );
  }

  private _loadMarketingDetails(): void {
    // PLANS-22888: URL wins over selectedLevel.caption so the API call succeeds even when the
    // level lookup was skipped (cross-session bookmark, removed template).
    const urlCoverageLevelName = this._route.snapshot.queryParamMap.get('coverageLevelName');
    const params: MarketingDataRequest = {
      practice: this._planInfoDetails['practiceId'],
      template: this._planInfoDetails['templateId'],
      coverageLevelName: urlCoverageLevelName || this.eventsManager.selectedLevel?.caption,
      pageName: this._planInfoDetails['pageName']
    };

    this.template = this.eventsManager.officeInfo?.benefitPlanTemplates?.find(
      (benefitPlanTemplate: BenefitPlanTemplateModel) => benefitPlanTemplate.benefitPlanTemplateId === this._planInfoDetails['templateId']
    );
    this.coverageLevel = this.template?.coverageLevels?.find(
      (level: CoverageLevelModel) => level.benefitPlanCoverageLevelId === this.eventsManager.selectedLevel.benefitPlanCoverageLevelId
    );

    this._salesService.getMarketingDetails(params)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result: MarketingDetailsApiResponseInterface) => {
        this.details = result;
      });
  }
}
